import { useContext, useEffect } from 'react';
import { useLocation, useNavigate } from 'react-router-dom';
import PropTypes from 'prop-types';
import { inject, observer, PropTypes as MobXPropTypes } from 'mobx-react';
import queryString from 'query-string';
import Table from 'react-bootstrap/Table';
import CounterpartiesTabTableFiltersBar from './components/CounterpartiesTabTableFiltersBar';
import CounterpartiesTabTableHead from './components/CounterpartiesTabTableHead';
import CounterpartiesTabTableRow from './components/CounterpartiesTabTableRow';
import { DIRECTION } from 'components/constants';
import i18nContext from 'components/i18n-context';
import EmptyTableContentPlug from 'components/EmptyTableContentPlug';
import Loader from 'components/Loader';
import './CounterpartiesTab.scss';

const CounterpartiesTab = ({ counterpartiesStore, customerNumber, downloadDocument }) => {
  const i18n = useContext(i18nContext);
  const location = useLocation();
  const navigate = useNavigate();

  const updateFiltersInUrl = () => {
    const filtersParams = counterpartiesStore.prepareCustomerCounterpartiesFiltersParams();
    const queryParams = {
      ...filtersParams
    };
    queryParams.tab = 'counterparties';

    const searchParams = queryString.stringify(queryParams);
    navigate({ search: `?${searchParams}` }, { replace: true });
  };

  const loadFiltersFromUrl = () => {
    const params = queryString.parse(location.search);
    counterpartiesStore.setCustomerCounterpartiesFiltersFromUrl(params);
  };

  useEffect(() => {
    loadFiltersFromUrl();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  useEffect(() => {
    if (!counterpartiesStore.isInitialized) {
      counterpartiesStore.getCustomerCounterpartiesList(customerNumber);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [counterpartiesStore.isInitialized]);

  useEffect(() => {
    if (counterpartiesStore.isInitialized) {
      counterpartiesStore.resetCounterpartiesStore();
      loadFiltersFromUrl();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [location.pathname]);

  useEffect(() => {
    updateFiltersInUrl(
      counterpartiesStore.customerCounterpartiesFilters,
      counterpartiesStore.customerCounterpartiesSortColumn
    );
    if (counterpartiesStore.isInitialized) {
      counterpartiesStore.getCustomerCounterpartiesList(customerNumber);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [
    counterpartiesStore.customerCounterpartiesFilters.from_date,
    counterpartiesStore.customerCounterpartiesFilters.to_date,
    counterpartiesStore.customerCounterpartiesFilters.direction,
    counterpartiesStore.customerCounterpartiesFilters.show_internal,
    counterpartiesStore.customerCounterpartiesSortColumn.sort_column,
    counterpartiesStore.customerCounterpartiesSortColumn.sort_direction,
    customerNumber
  ]);

  const handleGenerateMonitoringReport = async () => {
    await counterpartiesStore.exportCounterpartiesMonitoringReportCsv(customerNumber);
  };

  const handleOnSortTable = (newSortBy) => {
    counterpartiesStore.setCustomerCounterpartiesSortData({
      sortBy: newSortBy,
      direction:
        counterpartiesStore.customerCounterpartiesSortColumn.sort_column !== newSortBy
          ? DIRECTION.ASC
          : counterpartiesStore.customerCounterpartiesSortColumn.sort_direction === DIRECTION.ASC
            ? DIRECTION.DESC
            : DIRECTION.ASC
    });
  };

  if (!counterpartiesStore.isInitialized) {
    return <Loader className={'application-loader'} />;
  }

  return (
    <div className={'counterparties-tab-container'}>
      <CounterpartiesTabTableFiltersBar
        isLoading={counterpartiesStore.isLoading}
        isInitialized={counterpartiesStore.isInitialized}
        filters={counterpartiesStore.customerCounterpartiesFilters}
        handleSetFilters={counterpartiesStore.setCustomerCounterpartiesFilter}
        handleGenerateMonitoringReport={handleGenerateMonitoringReport}
      />

      {counterpartiesStore.customerCounterpartiesList?.length === 0 ? (
        <EmptyTableContentPlug
          headerText={i18n.getMessage('counterparties.customer.emptyPlug.header')}
          descriptionText={i18n.getMessage('counterparties.customer.emptyPlug.description')}
        />
      ) : (
        <>
          <Table responsive>
            <thead>
              <CounterpartiesTabTableHead
                handleOnSortTable={handleOnSortTable}
                sortBy={counterpartiesStore.customerCounterpartiesSortColumn.sort_column}
                direction={counterpartiesStore.customerCounterpartiesSortColumn.sort_direction}
              />
            </thead>
            <tbody>
              {counterpartiesStore.customerCounterpartiesList?.map((counterparty, index) => (
                <CounterpartiesTabTableRow
                  key={index}
                  type={'list'}
                  isLoading={counterpartiesStore.isLoading}
                  counterpartyData={counterparty}
                  handleDownloadDocument={downloadDocument}
                />
              ))}
            </tbody>
          </Table>
        </>
      )}
    </div>
  );
};

CounterpartiesTab.propTypes = {
  counterpartiesStore: MobXPropTypes.observableObject,
  customerNumber: PropTypes.string,
  downloadDocument: PropTypes.func
};

export default inject((stores) => ({
  counterpartiesStore: stores.counterpartiesStore
}))(observer(CounterpartiesTab));
